import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// La finestra «Dati dell'impresa» di ImpresaCard (pages/ProjectDetail.js:242-571, 31a5bd1).
//
// Si apre con «Modifica» o con l'evento "pagina-progetto:apri-impresa" su window (detail.campo: il primo dato
// mancante), una volta per richiesta: tornando al passo 1 da un altro riquadro non si riapre da sé (collaudo
// 16/10/2026). start(): il form riletto dal server (GET, la finestra del progetto di oggi), altrimenti la copia della
// pagina; poi il campo del dato mancante in vista 150 ms dopo l'apertura (vaiAlCampo).
//
// Salva: solo i campi cambiati da quando la finestra si è aperta (data-orig = il valore salvato; le proposte di
// denominazione e codice fiscale contano come cambiate), i sotto-campi con il loro oggetto; le sedi, se cambiate
// (codici e «va aperta»), prima e solo con la sede operativa. Un invio PUT JSON; la risposta (Turbo Stream) porta i
// toast e un refresh con morph della pagina (onSaved: la scheda, il percorso e il primo dato mancante si rileggono).

// cleanPiva / pivaValida di lib/bandoMeta.js
const cleanPiva = (value) => {
  let s = String(value || "").replace(/[\s.]/g, "").toUpperCase()
  if (s.startsWith("IT")) s = s.slice(2)
  return s
}
const pivaValida = (piva) => {
  if (!/^\d{11}$/.test(piva || "")) return false
  let total = 0
  for (let i = 0; i < 10; i++) {
    let d = Number(piva[i])
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9 }
    total += d
  }
  return (10 - (total % 10)) % 10 === Number(piva[10])
}
// avvisoDimensione (lo stesso di ImpresaHelper.avviso_dimensione per la scheda)
const avvisoDimensione = (dim, addetti) => {
  if (!dim || addetti === null || addetti === undefined || addetti === "") return ""
  const n = Number(addetti)
  if (n < 10 && dim !== "micro") return `Verifica la dimensione: con meno di 10 addetti (${n}) potrebbe essere micro.`
  if (n >= 10 && dim === "micro") return `Verifica la dimensione: con ${n} addetti non è micro (micro: meno di 10).`
  if (n >= 50 && dim === "piccola") return `Verifica la dimensione: con ${n} addetti non è piccola (piccola: meno di 50).`
  if (n >= 250 && ["micro", "piccola", "media"].includes(dim)) return `Verifica la dimensione: con ${n} addetti è grande (PMI: meno di 250).`
  return ""
}
const dataIt = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || "") ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso || "")
const triTo = (v) => (v === "" ? null : v === "si")
const LR_CAMPI = ["lr_nome", "lr_cognome", "lr_cf", "lr_ruolo", "lr_data_nascita", "lr_luogo_nascita", "lr_indirizzo", "lr_civico",
  "lr_cap", "lr_comune", "lr_provincia"]
// il sotto-campo del form -> il campo del corpo di PUT /impresa
const CAMPO = { importo: "de_minimis_ricevuti", verificato_il: "de_minimis_ricevuti", nota: "de_minimis_ricevuti",
  sede_via: "sede_indirizzo", sede_civico: "sede_indirizzo", sede_cap: "sede_indirizzo",
  sop_via: "sede_operativa_indirizzo", sop_civico: "sede_operativa_indirizzo", sop_cap: "sede_operativa_indirizzo", cciaa_prov: "cciaa", cciaa_rea: "cciaa",
  ...Object.fromEntries(LR_CAMPI.map((k) => [k, "legale_rappresentante"])) }
const codiceSede = (x) => JSON.stringify([x.sede_legale?.code || "", x.sede_operativa?.code || "", !!x.sede_operativa_da_aprire])

export default class extends Controller {
  static targets = ["form", "campo", "sedi", "propostaCostituita", "propostaCostituitaTesto", "pivaErrore", "cfProposta", "requisiti",
    "dataIscrizione", "propostaRegistro", "avvisoDimensione", "settoreAvviso", "salva", "attesa"]
  static values = { url: String, finestra: String, campi: Object }

  connect() {
    this.saving = false
    if (this.hasFormTarget) this.aggiorna()
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  // "pagina-progetto:apri-impresa" su window: la richiesta della pagina, con il primo dato mancante
  segnale(event) {
    this.start(event.detail?.campo || "")
  }

  // «Modifica»: anche così il primo dato mancante va in vista (useEffect([open, campo]) con campo = primoMancante della
  // pagina, sempre passato): il valore della pagina, che il morph dopo un salvataggio tiene aggiornato
  apri() {
    this.start(document.querySelector('[data-testid="project-detail"]')?.dataset.paginaProgettoCampoValue || "")
  }

  async start(campo) {
    try {
      const res = await fetch(this.urlValue, { credentials: "same-origin", headers: { Accept: "text/vnd.turbo-stream.html" } })
      if (!res.ok || res.redirected) throw new Error("risposta")
      const tpl = new DOMParser().parseFromString(await res.text(), "text/html").querySelector('turbo-stream[target="impresa-form"] template')
      if (!tpl) throw new Error("risposta")
      this.formTarget.replaceWith(tpl.content.cloneNode(true))
    } catch {
      this.ripristina()      // la copia della pagina, se il server non risponde
    }
    this.aggiorna()
    window.dispatchEvent(new CustomEvent("dialog:open", { detail: { id: this.finestraValue } }))
    clearTimeout(this.timer)
    if (campo) this.timer = setTimeout(() => this.vaiAlCampo(campo), 150)
  }

  // i valori com'erano resi nella pagina
  ripristina() {
    this.campoTargets.forEach((el) => {
      if (el.tagName === "SELECT") [...el.options].forEach((o) => { o.selected = o.defaultSelected })
      else el.value = el.defaultValue
    })
  }

  // vaiAlCampo: il campo del dato mancante in vista, con il cursore
  vaiAlCampo(etichetta) {
    const el = document.querySelector(`[data-testid="${this.campiValue[etichetta]}"]`)
    if (!el) return false
    el.scrollIntoView?.({ behavior: "smooth", block: "center" })
    // i campi nascosti delle sedi (il valore in JSON) non prendono il cursore: il primo campo visibile
    const input = el.matches("input, select, textarea") ? el : el.querySelector("input:not([type=hidden]), select, textarea")
    input?.focus?.()
    return true
  }

  cambia(event) {
    const el = event.target
    if (el.dataset.soloCifre) el.value = el.value.replace(/[^0-9]/g, "")
    this.aggiorna()
  }

  campoEl(nome) {
    return this.campoTargets.find((el) => el.name === nome)
  }

  valori() {
    const f = {}
    this.campoTargets.forEach((el) => { f[el.name] = el.value })
    if (this.hasDataIscrizioneTarget) f._data_iscrizione = this.dataIscrizioneTarget.value
    return f
  }

  pivaErr(f) {
    const piva = cleanPiva(f.partita_iva)
    if (!piva || pivaValida(piva)) return ""
    return /^\d{11}$/.test(piva) ? "La cifra di controllo non torna: ricontrolla la partita IVA." : "Servono 11 cifre."
  }

  aggiorna() {
    const f = this.valori()
    const err = this.pivaErr(f)
    this.pivaErroreTarget.textContent = err
    this.pivaErroreTarget.hidden = !err
    this.campoEl("partita_iva")?.classList.toggle("border-rose-400", !!err)
    this.salvaTarget.disabled = this.saving || !!err
    this.requisitiTarget.hidden = f.societa_costituita !== "si"
    const proposta = f.societa_costituita === "" && !!f.data_costituzione
    this.propostaCostituitaTarget.hidden = !proposta
    if (proposta) this.propostaCostituitaTestoTarget.textContent = `Data di costituzione ${dataIt(f.data_costituzione)}: la società risulta costituita.`
    this.propostaRegistroTarget.hidden = !(f.iscritta_registro === "" && f._data_iscrizione)
    this.cfPropostaTarget.hidden = !(this.formTarget.dataset.cfSalvato !== "true" && f.codice_fiscale && f.codice_fiscale === cleanPiva(f.partita_iva || ""))
    const avviso = avvisoDimensione(f.dimensione, f.addetti)
    this.avvisoDimensioneTarget.textContent = avviso
    this.avvisoDimensioneTarget.hidden = !avviso
    this.settoreAvvisoTarget.hidden = !f.settore_speciale
  }

  // «Usa «Sì»» della data di costituzione (anche l'iscrizione, se c'è la sua data)
  usaCostituita() {
    const f = this.valori()
    this.campoEl("societa_costituita").value = "si"
    this.campoEl("iscritta_registro").value = f.iscritta_registro || (f._data_iscrizione ? "si" : "")
    this.aggiorna()
  }

  usaRegistro() {
    this.campoEl("iscritta_registro").value = "si"
    this.aggiorna()
  }

  sediCorrenti() {
    const leggi = (k) => {
      const v = this.sediTarget.querySelector(`input[type=hidden][name="sedi[${k}]"]`)?.value
      return v ? JSON.parse(v) : null
    }
    const daAprire = this.sediTarget.querySelector('input[type=checkbox][name="sedi[sede_operativa_da_aprire]"]')
    return { sede_legale: leggi("sede_legale"), sede_operativa: leggi("sede_operativa"), sede_operativa_da_aprire: !!daAprire?.checked }
  }

  async salva() {
    const f = this.valori()
    if (this.pivaErr(f)) { window.toast?.error("Partita IVA non valida"); return }
    const orig = Object.fromEntries(this.campoTargets.map((el) => [el.name, el.dataset.orig ?? ""]))
    const tutti = {
      partita_iva: cleanPiva(f.partita_iva), settore_speciale: f.settore_speciale,
      societa_costituita: f.societa_costituita === "" ? null : f.societa_costituita === "si",
      iscritta_registro: triTo(f.iscritta_registro), startup_innovativa: triTo(f.startup_innovativa),
      dimensione: f.dimensione || "", data_costituzione: f.data_costituzione || "", stato_impresa: f.stato_impresa || "",
      addetti: f.addetti === "" ? null : Number(f.addetti),
      de_minimis_ricevuti: { importo: Number(f.importo) || 0, verificato_il: f.verificato_il || "", nota: f.nota || "" },
      pec: (f.pec || "").trim(), codice_fiscale: (f.codice_fiscale || "").trim(),
      legale_rappresentante: { nome: f.lr_nome || "", cognome: f.lr_cognome || "", codice_fiscale: f.lr_cf || "", ruolo: f.lr_ruolo || "",
        data_nascita: f.lr_data_nascita || "", luogo_nascita: f.lr_luogo_nascita || "",
        residenza: { indirizzo: f.lr_indirizzo || "", civico: f.lr_civico || "", cap: f.lr_cap || "", comune: f.lr_comune || "", provincia: f.lr_provincia || "" } },
      denominazione: (f.denominazione || "").trim(), forma_giuridica: f.forma_giuridica || "", email_impresa: (f.email_impresa || "").trim(), telefono: (f.telefono || "").trim(),
      sede_indirizzo: { indirizzo: f.sede_via || "", civico: f.sede_civico || "", cap: f.sede_cap || "" },
      sede_operativa_indirizzo: { indirizzo: f.sop_via || "", civico: f.sop_civico || "", cap: f.sop_cap || "" },
      cciaa: { provincia: f.cciaa_prov || "", rea: f.cciaa_rea || "" },
    }
    // solo i campi cambiati da quando la finestra si è aperta: un salvataggio non riscrive valori che non ha toccato
    const cambiati = new Set(Object.keys(orig).filter((k) => f[k] !== orig[k]).map((k) => CAMPO[k] || k))
    const impresa = Object.fromEntries(Object.entries(tutti).filter(([k]) => cambiati.has(k)))
    // prima le sedi, come sempre: stesso effetto di PUT /sede (territorio dei bandi, registro)
    const sedi = this.sediCorrenti()
    const sedeCambiata = codiceSede(sedi) !== codiceSede(JSON.parse(this.formTarget.dataset.sedi || "{}"))
    if (sedeCambiata && !sedi.sede_operativa) { window.toast?.error("Indica la sede operativa"); return }
    this.occupato(true)
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.urlValue, {
        method: "PUT", credentials: "same-origin", body: JSON.stringify({ sedi: sedeCambiata ? sedi : null, impresa }),
        headers: { "Content-Type": "application/json", Accept: "text/vnd.turbo-stream.html", ...(token ? { "X-CSRF-Token": token } : {}) },
      })
      if (res.redirected || !(res.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("risposta")
      const testo = await res.text()
      if (res.ok) window.dispatchEvent(new CustomEvent("dialog:close", { detail: { id: this.finestraValue } }))
      Turbo.renderStreamMessage(testo)
    } catch {
      window.toast?.error("Si è verificato un errore. Riprova.")    // formatApiErrorDetail(undefined)
    } finally {
      this.occupato(false)
    }
  }

  occupato(on) {
    this.saving = on
    this.attesaTarget.hidden = !on
    this.aggiorna()
  }
}
